import React, { FC, Fragment, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    IconButton,
    List,
    ListItem,
    ListItemText,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import { useFormik } from 'formik';
import { MillingMaterial } from 'models/milling';
import DeleteIcon from '@mui/icons-material/Delete';

type AddUpdateMillingMaterialDialogProps = {
    open: boolean;
    material?: MillingMaterial;
    onDialogClose: () => void;
    onAdd: (material: MillingMaterial) => void;
    onUpdate: (material: MillingMaterial) => void;
};

const AddUpdateMillingMaterialDialog: FC<AddUpdateMillingMaterialDialogProps> = ({
    open,
    onDialogClose,
    onAdd,
    onUpdate,
    material,
}: AddUpdateMillingMaterialDialogProps) => {
    const { t: translate } = useTranslation(['milling-jobs']);
    const [addColorName, setAddColorName] = useState<string>('');

    const validate = (values: MillingMaterial) => {
        const errors: Record<string, string> = {};
        if (!values.name) {
            errors.name = translate('milling-jobs:VALIDATION:NAME');
        }
        return errors;
    };

    const formik = useFormik({
        initialValues: {
            name: material?.name || '',
            additionalColors: material?.additionalColors || [],
            useVitaColors: material?.useVitaColors || false,
        },
        validateOnMount: true,
        validate,
        enableReinitialize: true,
        onSubmit: (values: MillingMaterial) => {
            if (material?._id) {
                onUpdate({ ...values, _id: material?._id });
            } else {
                onAdd({ ...values });
            }
        },
    });

    const handleDialogClose = () => {
        onDialogClose();
        setAddColorName('');
    };

    const removeColor = (index: number) => {
        const colors = [...formik.values.additionalColors];
        colors.splice(index, 1);
        formik.setFieldValue('additionalColors', colors);
    };

    const addColor = () => {
        formik.setFieldValue('additionalColors', [...formik.values.additionalColors, addColorName]);
        setAddColorName('');
    };

    return (
        <Dialog maxWidth="lg" fullWidth open={open}>
            {material?._id ? (
                <DialogTitle>{translate('milling-jobs:EDIT_MATERIAL')}</DialogTitle>
            ) : (
                <DialogTitle>{translate('milling-jobs:ADD_MATERIAL')}</DialogTitle>
            )}
            <form onSubmit={formik.handleSubmit}>
                <DialogContent>
                    <Box>
                        <FormControl margin="dense" fullWidth>
                            <TextField
                                id="name"
                                label={translate('common:NAME')}
                                required
                                helperText={formik.touched.name && formik.errors.name}
                                error={formik.touched.name && Boolean(formik.errors.name)}
                                {...formik.getFieldProps('name')}
                            />
                        </FormControl>
                    </Box>
                    <Box mb={2}>
                        <FormControlLabel
                            id="useVitaColors"
                            name="useVitaColors"
                            control={<Checkbox checked={formik.values.useVitaColors} onChange={formik.handleChange} />}
                            label={translate('milling-jobs:USE_VITA_COLORS')}
                        />
                    </Box>
                    <Typography mb={1} variant="h4">
                        {translate('milling-jobs:ADDITIONAL_COLORS')}
                    </Typography>
                    <Box mb={1}>
                        <Paper variant="outlined">
                            <List dense>
                                {formik.values.additionalColors.length > 0 ? (
                                    <Fragment>
                                        {formik.values.additionalColors.map((color, index) => (
                                            <ListItem
                                                key={index}
                                                secondaryAction={
                                                    <IconButton edge="end" onClick={() => removeColor(index)}>
                                                        <DeleteIcon />
                                                    </IconButton>
                                                }
                                            >
                                                <ListItemText primary={color} />
                                            </ListItem>
                                        ))}
                                    </Fragment>
                                ) : (
                                    <Box p={1}>
                                        <Alert severity="info">{translate('milling-jobs:NO_ADDITIONAL_COLORS')}</Alert>
                                    </Box>
                                )}
                            </List>
                        </Paper>
                    </Box>
                    <Box display="flex" alignItems="center">
                        <TextField
                            margin="dense"
                            label={translate('milling-jobs:ADD_ADDITIONAL_COLOR')}
                            sx={{ minWidth: 300 }}
                            value={addColorName}
                            onChange={(event) => {
                                setAddColorName(event.target.value);
                            }}
                        />

                        <Button disabled={!addColorName} onClick={() => addColor()}>
                            {translate('common:ADD')}
                        </Button>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDialogClose}>{translate('common:CANCEL')}</Button>
                    {material?._id ? (
                        <Button disabled={!formik.isValid} variant="contained" type="submit">
                            {translate('common:UPDATE')}
                        </Button>
                    ) : (
                        <Button disabled={!formik.isValid} variant="contained" type="submit">
                            {translate('common:ADD')}
                        </Button>
                    )}
                </DialogActions>
            </form>
        </Dialog>
    );
};

export default AddUpdateMillingMaterialDialog;
